import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { CrmOptionalWaitlistForm } from '@/components/waitlist/CrmOptionalWaitlistForm';
import { CheckCircle2, Database, FolderKanban, ArrowRightLeft, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    q: 'Do I need a CRM to use NoQ5?',
    a: "No. NoQ5 runs standalone on its own EU-hosted Postgres store, with a live Google Drive mirror of every contact, deal, meeting and activity — yours to export at any time. When you're ready for HubSpot, Salesforce or Pipedrive, we push the full history over. No re-entry, no lost context.",
  },
  {
    q: 'Is NoQ5 a CRM replacement?',
    a: "NoQ5 is CRM-optional, not CRM-hostile. For small teams we're the operating layer that runs sales end-to-end without one. For teams that already have HubSpot or Salesforce, we're the AI Chief of Staff that keeps it clean and updated. Same product, same €59/user/month.",
  },
  {
    q: 'How does the Google Drive mirror work?',
    a: 'Every record NoQ5 writes to its internal store is mirrored to a Google Sheet in your own Google Drive within seconds — contacts, deals, meetings, activities, notes. You own the file, you control access, you can export or hand it to any CRM in one click.',
  },
  {
    q: 'What happens when I later add HubSpot or Salesforce?',
    a: "OAuth into the CRM from your NoQ5 settings and we push every historical record across, mapped to native fields, with the full activity timeline preserved. From that moment on, NoQ5 writes to both sides so nothing gets stuck in the middle.",
  },
  {
    q: 'How much does it cost?',
    a: '€59 per user per month, billed annually. Same price with or without a CRM. Self-serve checkout is coming; today we set you up personally within one business day.',
  },
  {
    q: 'Who is this for?',
    a: 'Founders and small revenue teams who want sales infrastructure that works from day one, without a CRM contract or an admin. And any growing team that wants a clean migration path into HubSpot, Salesforce or Pipedrive whenever they need it.',
  },
];

const CrmOptional = () => {
  return (
    <Layout>
      <SEOHead
        title="Run sales without a CRM — NoQ5 CRM-optional"
        description="NoQ5 works with or without a CRM. Start on our EU-hosted store with a live Google Drive mirror; move to HubSpot, Salesforce or Pipedrive whenever you're ready — full history included."
        keywords="crm optional, sales without a crm, alternative to hubspot for startups, no crm needed, google sheets crm, lightweight crm alternative, noq5 crm optional"
        canonicalUrl="https://noq5.ai/crm-optional"
      />

      <Breadcrumbs items={[{ label: 'CRM-optional', href: '/crm-optional' }]} />

      <div className="max-w-4xl mx-auto px-4 py-12 md:py-20">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
          <Sparkles className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-semibold text-primary">Early access · CRM-optional</span>
        </div>

        <h1 className="text-4xl md:text-6xl font-extrabold leading-[1.05] mb-6">
          Run sales without a CRM.<br />
          <span className="text-gradient-teal">Add one whenever you're ready.</span>
        </h1>

        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mb-8 leading-relaxed">
          Most small teams don't need a CRM contract on day one — they need pipeline they can trust. NoQ5 runs
          standalone on its own EU-hosted store, mirrors every record to a Google Sheet in your own Drive, and
          pushes the full history into HubSpot, Salesforce or Pipedrive the moment you outgrow us. Same product,
          same €59 per user per month.
        </p>

        <div className="grid md:grid-cols-3 gap-3 mb-12">
          {[
            { icon: Database, title: 'EU-hosted Postgres store', body: 'AES-256 encryption, GDPR controls, full audit trail — your workspace, not a spreadsheet.' },
            { icon: FolderKanban, title: 'Live Google Drive mirror', body: 'Every contact, deal, meeting and activity mirrored to a Sheet in your Drive. You own the file.' },
            { icon: ArrowRightLeft, title: 'One-click CRM migration', body: 'Connect HubSpot, Salesforce or Pipedrive later. We push the history across, fields mapped, timeline intact.' },
          ].map((f, i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-5">
              <f.icon className="w-5 h-5 text-primary mb-2.5" />
              <p className="text-sm font-bold text-foreground mb-1">{f.title}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>

        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">How the CRM-optional workspace works</h2>
          <ul className="space-y-3 text-foreground/90">
            {[
              'NoQ5 listens to your calls, emails and meetings and drafts every contact, deal and activity update the way it always does.',
              'Approved updates land in your own EU-hosted Postgres workspace — accounts, deals, stages, activities, notes, next steps.',
              'The same records are mirrored, within seconds, to a Google Sheet in your Google Drive. You keep the file. You keep the export.',
              "When you're ready to add HubSpot, Salesforce or Pipedrive, OAuth in — we push the full history across and start writing to both sides.",
              "You're never locked in: the Google Drive mirror is yours forever, and every record can be exported as CSV.",
            ].map((line, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-14 bg-card border border-border rounded-2xl p-6 md:p-8">
          <div className="flex items-start gap-3 mb-3">
            <ShieldCheck className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground mb-2">Not a replacement. An alternative until you need one.</h2>
              <p className="text-muted-foreground leading-relaxed">
                We're not here to argue that a €50k/year HubSpot bill is wrong for every company — it isn't. For
                startups and small teams, though, adding an admin and a CRM contract before you have repeatable
                pipeline is the wrong order. Start on NoQ5, keep every record in your own Drive, and add HubSpot,
                Salesforce or Pipedrive the day it actually helps. We'll carry the history over.
              </p>
            </div>
          </div>
        </section>

        <section id="early-access" className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Request early access</h2>
          <p className="text-muted-foreground mb-6">
            We onboard CRM-optional workspaces personally today. Tell us your setup and we'll come back within one
            business day.
          </p>
          <CrmOptionalWaitlistForm />
        </section>

        <section className="mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">Frequently asked</h2>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-foreground mb-2">{f.q}</h3>
                <p className="text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <p className="text-sm text-muted-foreground">
          Already on a CRM? See <Link to="/pricing" className="text-primary underline">pricing</Link> or the
          {' '}<Link to="/ai-chief-of-staff-for-revenue" className="text-primary underline">AI Chief of Staff</Link> overview.
        </p>
      </div>
    </Layout>
  );
};

export default CrmOptional;
